import { ChevronDownIcon, ChevronLeftIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Fade,
  Flex,
  HStack,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  useToast
} from '@chakra-ui/react';
import exportFromJSON from 'export-from-json';
import { useEffect, useState } from 'react';
import { HiPlusCircle } from 'react-icons/hi';
import { useLocation, useParams } from 'react-router-dom';
import { Column } from 'react-table';

import { InteractiveTable } from '../../../components/data/InteractiveTable';
import { api } from '../../../services/api';
import { apiSensedia } from '../../../services/api-sensedia';

interface IQuotationClient {
  email?: string;
  insurancePremium: number;
  name: string;
  sellerId: string;
  sellerName: string;
  loginCode: string;
  period: string;
  periodText: string;
  payment?: string;
  phone?: string;
  birthday: string;
  status: string;
  proposta?: number;
  professional?: string;
}

export const ContrataFacilClientes = (): JSX.Element => {
  const toast = useToast();
  const { status } = useParams<{ status?: string }>();
  const { search } = useLocation();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loading, setLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);

  const [data, setData] = useState<IQuotationClient[]>([
    {
      email: '',
      insurancePremium: 0,
      name: '',
      sellerId: '',
      sellerName: '',
      loginCode: '',
      period: '',
      periodText: '',
      phone: '',
      birthday: '',
      status: ''
    }
  ]);

  const [itemsCount, setItemsCount] = useState(0);
  const [itemsToShow, setItemsToShow] = useState(5);

  const columns: Column<object>[] = [
    {
      Header: 'Nome do vendedor',
      accessor: 'sellerName'
    },
    {
      Header: 'Nome do cliente',
      accessor: 'name'
    },
    {
      Header: 'Número da Proposta',
      accessor: 'proposta'
    },
    {
      Header: 'Valor do prêmio',
      accessor: 'insurancePremium',
      Cell: ({ row }: { row: { values: { insurancePremium: number } } }) => (
        <>
          {row.values.insurancePremium ? (
            <Text>
              R${' '}
              {parseFloat(
                row.values.insurancePremium.toFixed(2)
              ).toLocaleString('PT-BR')}
            </Text>
          ) : (
            ''
          )}
        </>
      )
    },
    {
      Header: 'Periodicidade do pagamento',
      accessor: 'periodText'
    },
    {
      Header: 'Tipo de Cobrança',
      accessor: 'payment'
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row }: { row: { values: { status: string } } }) => (
        <Text>{row.values.status}</Text>
      )
    }
  ];

  const columnsNaoEfetivado: Column<object>[] = [
    {
      Header: 'Nome do vendedor',
      accessor: 'sellerName'
    },
    {
      Header: 'Nome do cliente',
      accessor: 'name'
    },
    {
      Header: 'Data de nascimento',
      accessor: 'birthday',
      Cell: ({ row }: { row: { values: { birthday: number } } }) => (
        <Text>
          {new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(
            new Date(row.values.birthday)
          )}
        </Text>
      )
    },
    {
      Header: 'Profissão',
      accessor: 'professional'
    },
    {
      Header: 'Telefone',
      accessor: 'phone'
    },
    {
      Header: 'E-mail',
      accessor: 'email'
    }
  ];

  const getData = async (
    status: string,
    quotationStatusOperator = 'eq',
    dataInicio?: string | null,
    dataFim?: string | null
  ) => {
    setLoading(true);

    try {
      const token = localStorage.getItem('tokenPortal');

      const response =
        dataInicio && dataFim
          ? await apiSensedia.get(
              `/gestao-de-carteiras/contrata-facil/clientes?status=${status}&statusOperator=${quotationStatusOperator}&dataInicio=${dataInicio}&dataFim=${dataFim}`,
              {
                timeout: 0
              }
            )
          : await apiSensedia.get(
              `/gestao-de-carteiras/contrata-facil/clientes?status=${status}&statusOperator=${quotationStatusOperator}`,
              {
                timeout: 0
              }
            );

      if (response.data.data) {
        const query = new URLSearchParams(search);
        const vendedor = query.get('vendedor');
        const data = vendedor
          ? response.data.data.filter(
              (item: IQuotationClient) => item.loginCode === vendedor
            )
          : response.data.data;
        setData(data);

        setItemsCount(data.length > 10 ? 10 : data.length);
      }

      setLoading(false);
      setLoadingTable(false);
    } catch (e: any) {
      if (e?.response?.status === 401) {
        onOpen();
      } else {
        const errorObj = e?.toJSON();
        const hasStatus = errorObj?.status || e?.response?.data?.statusCode;
        const hasMessage = errorObj?.message || e?.response?.data?.message;
        toast({
          position: 'top',
          description: (
            <Box>
              <Text>
                {' '}
                Ocorreu um erro. Esvazie o cache do navegador, aguarde alguns
                minutos e tente fazer login novamente, se persistir, por favor
                entrar em contato.
              </Text>
              <Text>
                {hasStatus && (
                  <>
                    <br />
                    <b>Detalhes:</b>
                    <br />
                  </>
                )}
                {hasStatus && (
                  <>{errorObj.status ?? e.response.data.statusCode}</>
                )}
                {hasStatus && hasMessage && ' - '}
                {hasMessage && (
                  <>{e.response.data.message ?? errorObj.message}</>
                )}
              </Text>
            </Box>
          ),
          duration: 15000,
          isClosable: true,
          status: 'error'
        });
      }

      setLoading(false);
    }
  };

  const exportData = (ext: 'csv' | 'xls') => {
    const data =
      status === 'efetivado' ? formatDataEfetivado() : formatDataNaoEfetivado();
    const fileName = `${status}`;
    const exportType = exportFromJSON.types[ext];

    exportFromJSON({ data, fileName, exportType });
  };

  const formatDataEfetivado = () => {
    return data.map((item) => {
      return {
        nomeVendedor: item.sellerName,
        nomeCliente: item.name,
        numeroProposta: item.proposta,
        valorPremio: item.insurancePremium,
        periodicidade: item.periodText,
        tipoCobranca: item.payment,
        status: item.status
      };
    });
  };

  const formatDataNaoEfetivado = () => {
    return data.map((item) => {
      return {
        nomeVendedor: item.sellerName,
        nomeCliente: item.name,
        dataNascimento: new Intl.DateTimeFormat('pt-BR', {
          timeZone: 'UTC'
        }).format(new Date(item?.birthday)),
        profissao: item.professional,
        telefone: item.phone,
        email: item.email
      };
    });
  };

  useEffect(() => {
    const quotationStatus = 'QUOTATION_FINISHED';
    const quotationStatusOperator = status === 'nao-efetivado' ? 'diff' : 'eq';
    const query = new URLSearchParams(search);
    const initialDate = query.get('dataInicio');
    const endDate = query.get('dataFim');
    const dataInicial = initialDate ? initialDate.replaceAll('-', '/') : null;
    const dataFim = endDate ? endDate.replaceAll('-', '/') : null;

    getData(quotationStatus, quotationStatusOperator, dataInicial, dataFim);
  }, []);

  return (
    <Stack p="1.7vw 11.5vw" spacing={23}>
      <Stack spacing={7}>
        <Stack spacing={1}>
          <Link
            href="/corretor/vida-individual/painel-vendas-contrata-facil"
            w="4rem"
          >
            <HStack spacing={0}>
              <ChevronLeftIcon />
              <Text fontSize="1rem" color="#696969" fontWeight="bold">
                Voltar
              </Text>
            </HStack>
          </Link>
          <Flex justifyContent="space-between">
            <Box>
              <Text color="#00335E" fontWeight="bold" fontSize="1.2rem">
                Contrata Fácil: Base de Clientes{' '}
                {status === 'efetivado' ? 'efetivados' : '(não efetivadas)'}
              </Text>
              {status !== 'efetivado' && (
                <Text color="#696969" fontWeight="normal" fontSize="0.8rem">
                  OBS: Os dados aqui apresentados são apenas dos clientes que
                  concordaram com a política de privacidade da SulAmérica e
                  autorizaram o contato do corretor.
                </Text>
              )}
            </Box>
            <Menu>
              <MenuButton
                bg="none"
                variant="outline"
                border="1px"
                borderColor="#1B365D"
                color="#1B365D"
                fontSize="0.8rem"
                as={Button}
                rightIcon={<ChevronDownIcon />}
              >
                Exportar Dados
              </MenuButton>
              <MenuList zIndex={1000}>
                <MenuItem onClick={() => exportData('csv')}>
                  Baixar .csv
                </MenuItem>
                <MenuItem onClick={() => exportData('xls')}>
                  Baixar .xls
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </Stack>
      </Stack>

      {loading ? (
        <Flex w="100%" justify="center">
          <Spinner />
        </Flex>
      ) : (
        <Fade in={!loading}>
          <Stack
            bg="#fff"
            color="#00335E"
            borderRadius="1rem"
            p="1.4rem 1.1rem .8rem"
          >
            {loadingTable ? (
              <Flex w="100%" justify="center">
                <Spinner />
              </Flex>
            ) : (
              <>
                {status === 'efetivado' ? (
                  <InteractiveTable
                    columns={columns}
                    data={data}
                    itemsCount={itemsCount}
                  />
                ) : (
                  <InteractiveTable
                    columns={columnsNaoEfetivado}
                    data={data}
                    itemsCount={itemsCount}
                  />
                )}
              </>
            )}
          </Stack>
          <Flex
            w="100%"
            justify="flex-end"
            fontSize=".9rem"
            align="center"
            mt="2.1rem"
          >
            <Flex
              w={{ base: '100%', lg: '60%' }}
              justify={{ base: 'center', lg: 'space-between' }}
              flexDir={{ base: 'column', lg: 'row' }}
            >
              {!loadingTable && (
                <>
                  <Flex
                    align="center"
                    cursor="pointer"
                    justify="center"
                    onClick={() => {
                      setItemsCount(
                        itemsToShow + itemsCount > data.length
                          ? data.length
                          : itemsToShow + itemsCount
                      );
                    }}
                  >
                    <HiPlusCircle size={26} color="#00335E" />
                    <Text>
                      Mais itens (exibindo {itemsCount}/{data.length})
                    </Text>
                  </Flex>
                  <Box>
                    <Select
                      border="1px solid #00335E !important"
                      borderRadius="1rem !important"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                        setItemsToShow(parseInt(e.target.value))
                      }
                    >
                      <option value={5}>exibir mais 5 por vez</option>
                      <option value={10}>exibir mais 10 por vez</option>
                      <option value={20}>exibir mais 20 por vez</option>
                      <option value={40}>exibir mais 40 por vez</option>
                    </Select>
                  </Box>
                </>
              )}
            </Flex>
          </Flex>
        </Fade>
      )}
      <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent textAlign="center">
          <ModalHeader>Atenção!</ModalHeader>
          <ModalBody>
            <Text>Ops, sua sessão expirou.</Text>
            <Text>
              Por favor, faça novamente o login no Portal do Corretor.
            </Text>
          </ModalBody>

          <ModalFooter justifyContent="center">
            <Link href={process.env.REACT_APP_PORTAL_URL}>
              <Button bg="#FA6B25" color="#fff" colorScheme="orange">
                Voltar
              </Button>
            </Link>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};
